import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { TfService, isAuthConfigChangedError } from './tf-service.service';
import { Router } from '@angular/router';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isTokenRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  private tfService!: TfService;

  constructor(private injector: Injector, private router: Router) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.tfService) {
      this.tfService = this.injector.get(TfService);
    }

    // Add credentials to all requests
    request = request.clone({ withCredentials: true });

    // Skip refresh-on-401 for auth endpoints (login challenges, refresh, logout, status)
    const authPaths = [
      '/auth/login',
      '/auth/refresh',
      '/auth/logout',
      '/auth/status',
      '/auth/reset',
      '/auth/password-reset-complete',
    ];
    if (authPaths.some((path) => request.url.includes(`${this.tfService.host}${path}`))) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Handle 401 error and attempt token refresh
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isTokenRefreshing) {
      this.isTokenRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.tfService.refreshAccessToken().pipe(
        switchMap((response) => {
          this.isTokenRefreshing = false;
          this.refreshTokenSubject.next(true);

          // Retry the original request with updated cookies
          return next.handle(request);
        }),
        catchError((refreshError) => {
          this.isTokenRefreshing = false;
          this.refreshTokenSubject.next(false);

          // refreshAccessToken already logged out and redirected with the reason.
          if (isAuthConfigChangedError(refreshError)) {
            return throwError(() => refreshError);
          }

          // Redirect to login on refresh failure (refreshAccessToken already
          // logged out for 401/403; other failures, e.g. no refresh cookie, did not).
          const status = (refreshError as HttpErrorResponse)?.status;
          if (status !== 401 && status !== 403) {
            this.tfService.logout().subscribe({ error: () => {} });
          }
          this.router.navigate(['/auth/signin']);

          return throwError(() => refreshError);
        })
      );
    } else {
      // Wait until the token refresh completes, then retry the request.
      // If the refresh failed, the session is over: retrying would only trigger
      // another refresh and a second redirect (e.g. after AUTH_CONFIG_CHANGED).
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((refreshed) => {
          if (!refreshed) {
            return throwError(() => new HttpErrorResponse({
              status: 401,
              statusText: 'Session ended',
              url: request.url,
            }));
          }
          return next.handle(request);
        })
      );
    }
  }
}